import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Prohibit, Upload, Plus, Trash, MagnifyingGlass, FileXls, X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * UsedAmazonAccountsPage — Admin maintains a "do-not-use" list of Amazon
 * account emails. Allocator skips these during locking; existing locks are
 * released on upload.
 */
export default function UsedAmazonAccountsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [bySource, setBySource] = useState({});
  const [pendingReview, setPendingReview] = useState(0);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [reviewedFilter, setReviewedFilter] = useState('all'); // 'all' | 'pending' | 'reviewed'
  const [loading, setLoading] = useState(true);
  const [skip] = useState(0);
  const [selected, setSelected] = useState(new Set());

  // Upload dialog
  const [showUp, setShowUp] = useState(false);
  const [upMode, setUpMode] = useState('manual'); // 'manual' | 'excel'
  const [upFile, setUpFile] = useState(null);
  const [upText, setUpText] = useState('');
  const [upNotes, setUpNotes] = useState('');
  const [upBusy, setUpBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      params.set('skip', String(skip));
      params.set('limit', '500');
      if (sourceFilter !== 'all') params.set('source', sourceFilter);
      if (reviewedFilter !== 'all') params.set('reviewed', reviewedFilter);
      const { data } = await api.get(`/used-amazon-accounts?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setBySource(data.by_source || {});
      setPendingReview(data.pending_review || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Load fail');
    } finally {
      setLoading(false);
    }
  }, [search, skip, sourceFilter, reviewedFilter]);

  useEffect(() => { load(); }, [load]);

  const doUpload = async () => {
    if (upMode === 'excel' && !upFile) {
      toast.error('Excel/CSV file select karo');
      return;
    }
    if (upMode === 'manual' && !upText.trim()) {
      toast.error('Emails paste karo');
      return;
    }
    setUpBusy(true);
    setLastResult(null);
    try {
      if (upMode === 'excel') {
        const fd = new FormData();
        fd.append('file', upFile);
        if (upNotes.trim()) fd.append('notes', upNotes.trim());
        const { data } = await api.post('/used-amazon-accounts/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setLastResult(data);
        toast.success(`${data.inserted} naye accounts added · ${data.skipped} pehle se the · ${data.platform_marked} platform_accounts flagged · ${data.released_locks} locks released`);
      } else {
        const lines = upText
          .split(/[\n,;\s]+/)
          .map(s => s.trim())
          .filter(Boolean);
        const { data } = await api.post('/used-amazon-accounts/add', {
          emails: lines,
          notes: upNotes.trim() || undefined,
        });
        setLastResult(data);
        toast.success(`${data.inserted} naye accounts added · ${data.skipped} pehle se the · ${data.released_locks} locks released`);
      }
      setUpFile(null);
      setUpText('');
      setUpNotes('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload fail');
    } finally {
      setUpBusy(false);
    }
  };

  const doDelete = async (id, email) => {
    if (!window.confirm(`Unmark "${email}"? Allocator phir se assign kar sakta hai.`)) return;
    try {
      await api.delete(`/used-amazon-accounts/${id}`);
      toast.success('Unmarked');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete fail');
    }
  };

  const doReview = async (id, email, action) => {
    const label = action === 'approve' ? 'Approve & Unblock' : 'Reject (keep blocked, mark reviewed)';
    if (!window.confirm(`${label} for ${email}?`)) return;
    try {
      const { data } = await api.post(`/used-amazon-accounts/${id}/review`, { action });
      toast.success(action === 'approve' ? `✅ Approved — ${email} ab dubara assign hoga` : `🔒 Rejected — ${email} blocked rahega`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Review fail');
    }
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Unmark ${selected.size} accounts? Phir se assign ho sakte hain.`)) return;
    try {
      const { data } = await api.post('/used-amazon-accounts/bulk-delete', { ids: Array.from(selected) });
      toast.success(`${data.deleted} unmarked`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete fail');
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }); }
    catch { return '—'; }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto" data-testid="used-amazon-accounts-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Prohibit size={28} weight="fill" className="text-rose-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Used Amazon Accounts</h1>
            <p className="text-xs text-gray-500">
              Allocator yaha listed emails ko <span className="font-semibold text-rose-700">skip</span> karega.
              Upload se existing locks bhi release ho jaate hain.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowUp(true)} className="bg-rose-600 hover:bg-rose-700 gap-1" data-testid="ua-open-upload-btn">
            <Upload size={16} /> Add / Upload
          </Button>
        </div>
      </div>

      {/* Stats strip — 5 tiles incl Pending Review */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <div className="bg-rose-50 border-2 border-rose-300 rounded p-3" data-testid="ua-total-tile">
          <div className="text-[10px] uppercase font-bold text-rose-700">Total Blocked</div>
          <div className="text-2xl font-extrabold text-rose-900">{total}</div>
        </div>
        <button
          type="button"
          onClick={() => { setSourceFilter('auto_swap'); setReviewedFilter('pending'); }}
          className="bg-orange-50 border-2 border-orange-400 rounded p-3 text-left hover:bg-orange-100 transition"
          data-testid="ua-pending-review-tile"
          title="Click to filter — swapped accounts awaiting admin review"
        >
          <div className="text-[10px] uppercase font-bold text-orange-700">⏳ Pending Review</div>
          <div className="text-2xl font-extrabold text-orange-900">{pendingReview}</div>
          <div className="text-[9px] text-orange-700">Auto-swapped by staff</div>
        </button>
        <button
          type="button"
          onClick={() => { setSourceFilter('auto_swap'); setReviewedFilter('all'); }}
          className="bg-amber-50 border border-amber-200 rounded p-3 text-left hover:bg-amber-100 transition"
        >
          <div className="text-[10px] uppercase font-bold text-amber-700">⚡ Auto-Swap (All)</div>
          <div className="text-2xl font-extrabold text-amber-900">{bySource.auto_swap || 0}</div>
        </button>
        <button
          type="button"
          onClick={() => { setSourceFilter('excel_upload'); setReviewedFilter('all'); }}
          className="bg-blue-50 border border-blue-200 rounded p-3 text-left hover:bg-blue-100 transition"
        >
          <div className="text-[10px] uppercase font-bold text-blue-700">📊 Excel Uploads</div>
          <div className="text-2xl font-extrabold text-blue-900">{bySource.excel_upload || 0}</div>
        </button>
        <button
          type="button"
          onClick={() => { setSourceFilter('manual'); setReviewedFilter('all'); }}
          className="bg-emerald-50 border border-emerald-200 rounded p-3 text-left hover:bg-emerald-100 transition"
        >
          <div className="text-[10px] uppercase font-bold text-emerald-700">✍️ Manual Adds</div>
          <div className="text-2xl font-extrabold text-emerald-900">{bySource.manual || 0}</div>
        </button>
      </div>

      {/* Active filter chips */}
      {(sourceFilter !== 'all' || reviewedFilter !== 'all') && (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-600">Active filters:</span>
          {sourceFilter !== 'all' && (
            <button
              onClick={() => setSourceFilter('all')}
              className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-[11px] font-semibold flex items-center gap-1 hover:bg-blue-200"
            >
              Source: {sourceFilter} <X size={10} />
            </button>
          )}
          {reviewedFilter !== 'all' && (
            <button
              onClick={() => setReviewedFilter('all')}
              className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full text-[11px] font-semibold flex items-center gap-1 hover:bg-orange-200"
            >
              Review: {reviewedFilter} <X size={10} />
            </button>
          )}
          <button
            onClick={() => { setSourceFilter('all'); setReviewedFilter('all'); }}
            className="px-2 py-0.5 text-[11px] text-gray-600 hover:text-gray-900 underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Email search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="ua-search-input"
          />
        </div>
        {selected.size > 0 && (
          <Button variant="outline" onClick={doBulkDelete} className="gap-1 border-red-300 text-red-700" data-testid="ua-bulk-delete-btn">
            <Trash size={14} /> Unmark {selected.size} selected
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left w-8">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && selected.size === items.length}
                    onChange={toggleAll}
                    data-testid="ua-toggle-all"
                  />
                </th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Reason / Notes</th>
                <th className="px-3 py-2 text-left">Marked By</th>
                <th className="px-3 py-2 text-left">Marked At</th>
                <th className="px-3 py-2 text-left">Review</th>
                <th className="px-3 py-2 text-right w-32">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-gray-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-gray-500" data-testid="ua-empty">
                  Koi blocked account nahi hai. "Add / Upload" se shuru karo.
                </td></tr>
              ) : items.map((it) => (
                <tr key={it.id} className={`border-t hover:bg-rose-50/40 ${it.source === 'auto_swap' && !it.admin_reviewed ? 'bg-orange-50/40' : ''}`}>
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={selected.has(it.id)}
                      onChange={() => toggleSelect(it.id)}
                      data-testid={`ua-select-${it.id}`}
                    />
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-rose-900">{it.email}</td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                      it.source === 'auto_swap' ? 'bg-orange-100 text-orange-800' :
                      it.source === 'excel_upload' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {it.source === 'auto_swap' ? '⚡ Auto-Swap' : it.source === 'excel_upload' ? '📊 Excel' : '✍️ Manual'}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-xs text-gray-700 max-w-[260px]" title={it.notes}>
                    {it.reason_code && (
                      <span className="block font-semibold text-orange-800">{it.reason_code}</span>
                    )}
                    {it.reason_extra && <span className="block text-[10px] text-gray-600 italic">{it.reason_extra}</span>}
                    {!it.reason_code && (<span className="truncate inline-block max-w-full">{it.notes || '—'}</span>)}
                  </td>
                  <td className="px-3 py-1.5 text-xs">{it.marked_by_name || '—'}</td>
                  <td className="px-3 py-1.5 text-xs text-gray-600">{fmtDate(it.marked_at)}</td>
                  <td className="px-3 py-1.5 text-xs">
                    {it.source === 'auto_swap' ? (
                      it.admin_reviewed ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-semibold">✓ Reviewed</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-200 text-orange-900 font-bold animate-pulse">⏳ Pending</span>
                      )
                    ) : (
                      <span className="text-[10px] text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {it.source === 'auto_swap' && !it.admin_reviewed && (
                        <>
                          <button
                            onClick={() => doReview(it.id, it.email, 'approve')}
                            className="px-1.5 py-0.5 bg-emerald-600 text-white rounded text-[10px] font-bold hover:bg-emerald-700"
                            title="Approve — account dubara assign hoga"
                            data-testid={`ua-approve-${it.id}`}
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => doReview(it.id, it.email, 'reject')}
                            className="px-1.5 py-0.5 bg-gray-600 text-white rounded text-[10px] font-bold hover:bg-gray-700"
                            title="Mark reviewed but keep blocked"
                            data-testid={`ua-reject-${it.id}`}
                          >
                            ✗ Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => doDelete(it.id, it.email)}
                        className="text-rose-600 hover:text-rose-800"
                        title="Unmark (allocator ab use kar sakta hai)"
                        data-testid={`ua-delete-${it.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUp} onOpenChange={setShowUp}>
        <DialogContent className="max-w-2xl" data-testid="ua-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Prohibit className="text-rose-600" /> Add Used Amazon Accounts</DialogTitle>
          </DialogHeader>

          {/* Mode tabs */}
          <div className="flex gap-1 border rounded p-1 bg-gray-50 w-fit">
            <button
              type="button"
              onClick={() => setUpMode('manual')}
              className={`px-3 py-1 text-sm rounded ${upMode === 'manual' ? 'bg-rose-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              data-testid="ua-tab-manual"
            >
              <Plus size={14} className="inline mr-1" /> Manual Paste
            </button>
            <button
              type="button"
              onClick={() => setUpMode('excel')}
              className={`px-3 py-1 text-sm rounded ${upMode === 'excel' ? 'bg-rose-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              data-testid="ua-tab-excel"
            >
              <FileXls size={14} className="inline mr-1" /> Excel / CSV
            </button>
          </div>

          {upMode === 'manual' ? (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-700">Emails (one per line, ya comma-separated)</label>
              <textarea
                value={upText}
                onChange={(e) => setUpText(e.target.value)}
                rows={8}
                className="w-full border rounded p-2 text-sm font-mono"
                placeholder={'awearulg158@asxaz.com\nawearulg159@asxaz.com\nddagtjxm+164@gmail.com'}
                data-testid="ua-manual-textarea"
              />
              <div className="text-[10px] text-gray-500">
                Detected: {(upText.match(/[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g) || []).length} emails
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-700">Excel / CSV file</label>
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls,.csv,.txt"
                onChange={(e) => setUpFile(e.target.files?.[0] || null)}
                className="block w-full text-sm"
                data-testid="ua-file-input"
              />
              <div className="text-[10px] text-gray-500">
                Format: Column A me email list. Header optional. {upFile && (<span className="text-emerald-700 font-semibold">Selected: {upFile.name}</span>)}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-gray-700">Notes (optional)</label>
            <Input
              value={upNotes}
              onChange={(e) => setUpNotes(e.target.value)}
              placeholder="e.g. 'KYC failed batch' or 'Manual mark from 11-Feb-26'"
              data-testid="ua-notes-input"
            />
          </div>

          {lastResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs space-y-0.5" data-testid="ua-last-result">
              <div><strong>Parsed:</strong> {lastResult.parsed}</div>
              <div><strong>Inserted (new):</strong> <span className="text-emerald-800 font-bold">{lastResult.inserted}</span></div>
              <div><strong>Skipped (already marked):</strong> {lastResult.skipped}</div>
              <div><strong>Platform accounts flagged:</strong> {lastResult.platform_marked}</div>
              <div><strong>Existing locks released:</strong> {lastResult.released_locks}</div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUp(false)} disabled={upBusy}><X size={14} /> Cancel</Button>
            <Button onClick={doUpload} disabled={upBusy} className="bg-rose-600 hover:bg-rose-700" data-testid="ua-submit-upload">
              {upBusy ? 'Adding…' : (upMode === 'excel' ? 'Upload File' : 'Add Emails')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
